import { useMutationWithAccessCheckLog } from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { DESIGN_DOC_GROUPS_QUERY_KEY } from "@src/global_functions/postgrestApi/designDocsQueries";
import { useQueryClient } from "@tanstack/react-query";
import FetchClients, { ClientOutput } from "../client";
import { BackpackServiceError, isBackpackResponseErrorBody } from "../errors";

const document = {
  mutations: {
    usePostBulkShare,
    useDeleteFolder,
  },
} as const;

export default document;

export type BulkShareRequest = ClientOutput["d3"]["BulkShareReq"];

export const EMAIL_NOT_ON_ACCOUNT_ERROR_CODE = 1001;

// endpoint requires one of {feature: ["Documents", "Utilities", "Equipment", "Projects"], access: "read"}
function usePostBulkShare() {
  return useMutationWithAccessCheckLog(
    [["Documents", "Utilities", "Equipment", "Projects"], "read"],
    async (req: BulkShareRequest, checkFeatureAccessHeader) => {
      const { error, response } = await FetchClients.d3.POST(
        "/files/bulkshare",
        {
          body: req,
          headers: checkFeatureAccessHeader,
        }
      );
      if (response.ok) return;
      if (isBackpackResponseErrorBody(error)) {
        throw new BackpackServiceError(error);
      }
      throw new Error("Unknown error");
    }
  );
}

function useDeleteFolder(siteId: number) {
  const queryClient = useQueryClient();
  return useMutationWithAccessCheckLog(
    ["Documents", "edit"],
    async (folder_id: number, checkFeatureAccessHeader) => {
      const { error, response } = await FetchClients.d3.DELETE(
        "/design/folders/{folder_id}",
        {
          params: {
            path: {
              folder_id,
            },
          },
          headers: checkFeatureAccessHeader,
        }
      );
      if (response.ok) return;
      if (isBackpackResponseErrorBody(error)) {
        throw new BackpackServiceError(error);
      }
      throw error;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [DESIGN_DOC_GROUPS_QUERY_KEY, siteId],
        });
      },
    }
  );
}
